import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const ContentContext = createContext();

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};

// HEX rengi RGB'ye çevirme yardımcı fonksiyonu
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Rengi açma/koyulaştırma fonksiyonu
const adjustColor = (hex, amount) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  const adjust = (value) => Math.max(0, Math.min(255, value + amount));
  
  return `#${[rgb.r, rgb.g, rgb.b].map(v => {
    const adjusted = adjust(v);
    return adjusted.toString(16).padStart(2, '0');
  }).join('')}`;
};

// CSS değişkenlerini güncelleyen fonksiyon
const applyThemeColors = (primaryColor, secondaryColor) => {
  if (!primaryColor && !secondaryColor) return;
  
  const root = document.documentElement;
  
  if (primaryColor) {
    const primaryRgb = hexToRgb(primaryColor);
    root.style.setProperty('--primary', primaryColor);
    root.style.setProperty('--primary-light', adjustColor(primaryColor, 30));
    root.style.setProperty('--primary-dark', adjustColor(primaryColor, -30));
    
    // RGB değerlerini ayrı ayrı kaydet (rgba kullanımı için)
    if (primaryRgb) {
      root.style.setProperty('--primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
      const primaryLightRgb = hexToRgb(adjustColor(primaryColor, 30));
      if (primaryLightRgb) {
        root.style.setProperty('--primary-light-rgb', `${primaryLightRgb.r}, ${primaryLightRgb.g}, ${primaryLightRgb.b}`);
      }
      
      // Gradient'leri güncelle
      const primaryLight = adjustColor(primaryColor, 30);
      root.style.setProperty('--gradient-primary', `linear-gradient(135deg, ${primaryColor} 0%, ${primaryLight} 100%)`);
      root.style.setProperty('--gradient-hero', `linear-gradient(135deg, rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.95) 0%, rgba(${primaryRgb.r + 15}, ${primaryRgb.g + 17}, ${primaryRgb.b + 29}, 0.9) 50%, rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.95) 100%)`);
    }
  }
  
  if (secondaryColor) {
    const accentRgb = hexToRgb(secondaryColor);
    root.style.setProperty('--accent', secondaryColor);
    root.style.setProperty('--accent-light', adjustColor(secondaryColor, 30));
    root.style.setProperty('--accent-dark', adjustColor(secondaryColor, -30));
    root.style.setProperty('--warning', secondaryColor);
    
    // RGB değerlerini ayrı ayrı kaydet (rgba kullanımı için)
    if (accentRgb) {
      root.style.setProperty('--accent-rgb', `${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}`);
      const accentLightRgb = hexToRgb(adjustColor(secondaryColor, 30));
      if (accentLightRgb) {
        root.style.setProperty('--accent-light-rgb', `${accentLightRgb.r}, ${accentLightRgb.g}, ${accentLightRgb.b}`);
      }
      
      // Gradient güncelle
      const accentLight = adjustColor(secondaryColor, 30);
      root.style.setProperty('--gradient-accent', `linear-gradient(135deg, ${secondaryColor} 0%, ${accentLight} 100%)`);
      
      // Glow shadow güncelle
      root.style.setProperty('--shadow-glow', `0 0 40px rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.3)`);
    }
  }
};

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState({
    hero: {
      title: 'BMK Eğitim Kurumları',
      subtitle: 'Geleceğinizi Birlikte Şekillendiriyoruz',
      description: 'Uzman kadromuz ve modern eğitim anlayışımızla öğrencilerimizi başarıya taşıyoruz.',
      buttonText: 'Bursluluk Sınavına Başvur'
    },
    about: {
      title: 'Hakkımızda',
      description: 'BMK Eğitim Kurumları olarak 15 yılı aşkın tecrübemizle binlerce öğrencimizi hayallerindeki okullara yerleştirdik.',
      features: []
    },
    services: {
      title: 'Eğitim Programlarımız',
      items: []
    },
    stats: {
      items: [
        { number: '5000+', label: 'Mezun Öğrenci' },
        { number: '50+', label: 'Uzman Öğretmen' },
        { number: '15+', label: 'Yıllık Tecrübe' },
        { number: '%95', label: 'Başarı Oranı' }
      ]
    },
    contact: {
      title: 'İletişim',
      address: 'Atatürk Caddesi No: 123',
      phone: '0212 123 45 67',
      email: 'info@bmkegitim.com'
    },
    general: {
      siteName: 'BMK Eğitim Kurumları',
      primaryColor: '#1e3a5f',
      secondaryColor: '#f59e0b'
    }
  });
  const [loading, setLoading] = useState(true);

  const fetchContent = useCallback(async () => {
    try {
      const response = await api.get('/content');
      setContent(prev => {
        const newContent = { ...prev, ...response.data };
        
        // services.items'ı services.programs'a dönüştür (eski veri uyumluluğu)
        if (newContent.services?.items && !newContent.services?.programs) {
          newContent.services.programs = newContent.services.items.map((item, index) => ({
            id: item.id || index + 1,
            title: item.title || '',
            description: item.description || '',
            features: item.features || [],
            color: item.color || '#667eea'
          }));
        }
        
        // Renk ayarlarını uygula
        if (newContent.general) {
          applyThemeColors(newContent.general.primaryColor, newContent.general.secondaryColor);
        }
        
        return newContent;
      });
    } catch (error) {
      console.error('İçerik yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  // İlk yüklemede default renkleri uygula
  useEffect(() => {
    if (content.general) {
      applyThemeColors(content.general.primaryColor, content.general.secondaryColor);
    }
  }, []);

  const updateContent = async (section, data) => {
    try {
      await api.put(`/content/${section}`, { data });
      const updatedSection = { ...content[section], ...data };
      setContent(prev => ({
        ...prev,
        [section]: updatedSection
      }));
      
      // Eğer genel ayarlar güncellendiyse renkleri uygula
      if (section === 'general') {
        applyThemeColors(data.primaryColor || content.general?.primaryColor, data.secondaryColor || content.general?.secondaryColor);
      }
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Güncelleme başarısız' 
      };
    }
  };

  const refreshContent = () => {
    fetchContent();
  };

  const value = {
    content,
    loading,
    updateContent,
    refreshContent
  };

  return (
    <ContentContext.Provider value={value}>
      {children}
    </ContentContext.Provider>
  );
};

